import { useEffect } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { blogPosts } from "../data/blogPosts";

const ASSET_BASE = process.env.PUBLIC_URL || "";

interface BlogLayoutProps {
  /** Post id to exclude from related posts */
  postId: number;
  title: string;
  date: string;
  heroImage: string;
  children: React.ReactNode;
}

export default function BlogLayout({
  postId,
  title,
  date,
  heroImage,
  children,
}: BlogLayoutProps) {
  const relatedPosts = blogPosts.filter((p) => p.id !== postId).slice(0, 3);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <Box
      sx={{
        maxWidth: 900,
        mx: "auto",
        px: { xs: 2, md: 4 },
        pt: { xs: 10, md: 14 },
        pb: 10,
      }}
    >
      {/* Header */}
      <Box component="header" sx={{ textAlign: "left", mb: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: "2rem", md: "2.75rem" },
            fontWeight: 700,
            color: "text.primary",
            mb: 3,
          }}
        >
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: "grey.600" }}>
          {date}
        </Typography>
      </Box>

      {/* Hero Image */}
      <Box sx={{ mb: 6, mx: { xs: 0, md: -4 } }}>
        <Box sx={{ overflow: "hidden", borderRadius: 0 }}>
          <Box
            component="img"
            src={`${ASSET_BASE}/assets/${heroImage}`}
            alt={title}
            sx={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </Box>
      </Box>

      {/* Article body */}
      <Box component="article" sx={{ "& > *:not(:last-child)": { mb: 5 } }}>
        {children}
      </Box>

      {/* Share */}
      <Box sx={{ mt: 6, display: "flex", alignItems: "center" }}>
        <Box
          component="a"
          href="https://www.linkedin.com/sharing/share-offsite/?url=YOUR_BLOG_URL"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.5,
            px: 2.5,
            py: 1.2,
            borderRadius: "8px",
            bgcolor: "#e0e0e0",
            color: "#757575",
            textDecoration: "none",
            transition: "all 0.3s ease",
            cursor: "pointer",
            "&:hover": {
              bgcolor: "#e0e0e0",
              "& .share-text": {
                color: "#757575",
              },
              "& svg": {
                fill: "#b7127e",
              },
            },
          }}
        >
          <Typography
            variant="body2"
            className="share-text"
            sx={{ color: "#757575", fontWeight: 500 }}
          >
            Share
          </Typography>
          <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
        </Box>
      </Box>

      {/* Related Posts */}
      <Box sx={{ mt: 10 }}>
        <Typography
          variant="h4"
          sx={{ color: "primary.main", fontWeight: 500, mb: 4 }}
        >
          Related posts
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: 3, md: 5 },
          }}
        >
          {relatedPosts.map((post) => (
            <Box
              key={post.id}
              component={Link}
              to={post.href}
              sx={{
                display: "block",
                overflow: "hidden",
                borderRadius: "24px 24px 0px 24px",
                boxShadow: 1,
                transition: "box-shadow 0.3s, transform 0.3s",
                "&:hover": { boxShadow: 4, transform: "translateY(-4px)" },
                lineHeight: 0,
              }}
            >
              <Box
                sx={{ position: "relative", aspectRatio: "2/1", width: "100%" }}
              >
                <Box
                  component="img"
                  src={post.img.src}
                  alt={post.title}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    transition: "transform 0.5s",
                    "&:hover": { transform: "scale(1.05)" },
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
